import {
  clearAuthHeaders,
  loadAuthHeaders,
  saveAuthHeaders,
  type AuthHeaders,
} from './auth-storage';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = 'unauthorized', code: string | null = null) {
    super(401, message, code);
    this.name = 'UnauthorizedError';
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

const TOKEN_HEADER_KEYS: readonly (keyof AuthHeaders)[] = ['access-token', 'client', 'uid', 'expiry'];

// Unset in dev: paths stay relative and Vite proxies them to the local Rails
// server. Injected at build time for a deployed build.
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

function readTokenHeaders(res: Response): Partial<AuthHeaders> {
  const out: Partial<AuthHeaders> = {};
  for (const key of TOKEN_HEADER_KEYS) {
    const value = res.headers.get(key);
    if (value) out[key] = value;
  }
  return out;
}

// devise_token_auth rotates the token and sends all four headers together; a
// response without an access-token keeps the stored bundle as it is.
function persistRotatedHeaders(fromResponse: Partial<AuthHeaders>): void {
  const token = fromResponse['access-token'];
  if (!token) return;

  const current = loadAuthHeaders();
  const client = fromResponse.client ?? current?.client;
  const uid = fromResponse.uid ?? current?.uid;
  const expiry = fromResponse.expiry ?? current?.expiry;
  if (!client || !uid || !expiry) return;

  saveAuthHeaders({ 'access-token': token, client, uid, expiry });
}

async function parseError(res: Response): Promise<{ message: string; code: string | null }> {
  try {
    const json = (await res.json()) as {
      errors?: string[] | { full_messages?: string[] };
      message?: string;
      error?: string;
      error_code?: string;
    };
    const code = json.error_code ?? (typeof json.error === 'string' ? json.error : null);
    if (Array.isArray(json.errors) && json.errors.length) return { message: json.errors.join(', '), code };
    if (json.errors && 'full_messages' in json.errors && json.errors.full_messages?.length) {
      return { message: json.errors.full_messages.join(', '), code };
    }
    return { message: json.message ?? `HTTP ${res.status}`, code };
  } catch {
    return { message: `HTTP ${res.status}`, code: null };
  }
}

export async function apiRequest<T = unknown>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  const stored = loadAuthHeaders();
  // Without it the platform's password update answers a refused password with
  // a redirect to its HTML reset page instead of the JSON error to show.
  const headers = new Headers({ 'content-type': 'application/json', 'x-requested-with': 'XMLHttpRequest' });
  if (stored) {
    for (const key of TOKEN_HEADER_KEYS) headers.set(key, stored[key]);
  }

  const init: RequestInit = { method: opts.method ?? 'GET', headers };
  if (opts.signal) init.signal = opts.signal;
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  const res = await fetch(`${API_BASE}${path}`, init);
  const rotated = readTokenHeaders(res);

  if (res.status === 401) {
    clearAuthHeaders();
    const { message, code } = await parseError(res);
    throw new UnauthorizedError(message, code);
  }
  if (!res.ok) {
    const { message, code } = await parseError(res);
    throw new ApiError(res.status, message, code);
  }

  persistRotatedHeaders(rotated);

  const contentType = res.headers.get('content-type') ?? '';
  return (/\bjson\b/i.test(contentType) ? await res.json() : undefined) as T;
}
